import { motion, AnimatePresence, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";

/* ═══════════════════════════════════════════════════════════════
   MoneyMeet Pay — Motion System 2.0
   Premium fintech animation language
   ═══════════════════════════════════════════════════════════════ */

// ── Easing Curves ──────────────────────────────────────────────────────────────
export const ease = {
  out:      [0.0,  0.0,  0.2, 1],      // smooth decelerate
  in:       [0.4,  0.0,  1.0, 1],      // smooth accelerate
  inOut:    [0.4,  0.0,  0.2, 1],      // smooth in and out
  expo:     [0.16, 1,    0.3, 1],      // expo decelerate — brand primary easing
  decisive: [0.4,  0,    0.2, 1],      // confident response easing
  gentle:   [0.4,  0,    0.6, 1],      // ambient / ambient loops
  spring:   [0.175,0.885,0.32,1.275],  // slight overshoot
  bounce:   [0.34, 1.56, 0.64, 1],     // playful bounce
  back:     [0.68,-0.55, 0.265,1.55],  // back-up entrance
};

// Canonical Engineering Bible alias — ALWAYS import mmEase in components, never `ease` directly
export const mmEase = ease;

// Primary easing (backwards compat)
export const easeOut = ease.expo;

// ── Spring Configs ─────────────────────────────────────────────────────────────
export const springs = {
  stiff:    { type: "spring", stiffness: 500, damping: 30 },
  medium:   { type: "spring", stiffness: 300, damping: 28 },
  gentle:   { type: "spring", stiffness: 180, damping: 22 },
  bounce:   { type: "spring", stiffness: 400, damping: 18 },
  slow:     { type: "spring", stiffness: 120, damping: 20 },
  wobbly:   { type: "spring", stiffness: 280, damping: 14 },
};

// Canonical Engineering Bible spring presets
export const mmSpring = {
  default: { type: "spring", stiffness: 400, damping: 32 },
  soft:    { type: "spring", stiffness: 280, damping: 24 },
  snappy:  { type: "spring", stiffness: 500, damping: 40 },
};

// ── Duration Scale ─────────────────────────────────────────────────────────────
export const duration = {
  instant: 0.08,
  fast:    0.15,
  base:    0.22,
  slow:    0.38,
  slower:  0.55,
  slowest: 0.80,
};

// Canonical Engineering Bible duration scale (seconds, for Framer Motion)
export const mmDuration = {
  fast:     0.12,
  base:     0.20,
  slow:     0.34,
  slower:   0.52,
  ceremony: 0.88,
};

// ── Item Variants (children of stagger containers) ───────────────────────────
export const itemFadeUp = {
  hidden: { opacity: 0, y: 22, filter: "blur(2px)" },
  show:   { opacity: 1, y: 0,  filter: "blur(0px)",
    transition: { duration: duration.slower, ease: ease.expo } },
};

export const itemFadeLeft = {
  hidden: { opacity: 0, x: -24 },
  show:   { opacity: 1, x: 0,
    transition: { duration: duration.slower, ease: ease.expo } },
};

export const itemFadeRight = {
  hidden: { opacity: 0, x: 24 },
  show:   { opacity: 1, x: 0,
    transition: { duration: duration.slower, ease: ease.expo } },
};

export const itemScaleIn = {
  hidden: { opacity: 0, scale: 0.88, filter: "blur(4px)" },
  show:   { opacity: 1, scale: 1,    filter: "blur(0px)",
    transition: { duration: duration.slow, ease: ease.expo } },
};

export const itemSlideDown = {
  hidden: { opacity: 0, y: -16 },
  show:   { opacity: 1, y: 0,
    transition: { duration: duration.slow, ease: ease.expo } },
};

// ── Container Variants ────────────────────────────────────────────────────────
export const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};

export const staggerFastContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05, delayChildren: 0 } },
};

export const staggerSlowContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

// ── Page Transition Variants ──────────────────────────────────────────────────
export const pageVariants = {
  initial: { opacity: 0, y: 14, filter: "blur(4px)" },
  enter:   { opacity: 1, y: 0,  filter: "blur(0px)",
    transition: { duration: 0.45, ease: ease.expo } },
  exit:    { opacity: 0, y: -10, filter: "blur(2px)",
    transition: { duration: 0.22, ease: ease.in } },
};

export const pageSlideVariants = {
  initial: { opacity: 0, x: 32 },
  enter:   { opacity: 1, x: 0,
    transition: { duration: 0.38, ease: ease.expo } },
  exit:    { opacity: 0, x: -20,
    transition: { duration: 0.20, ease: ease.in } },
};

// ── Modal Variants ────────────────────────────────────────────────────────────
export const modalBackdrop = {
  hidden: { opacity: 0 },
  show:   { opacity: 1, transition: { duration: 0.20 } },
  exit:   { opacity: 0, transition: { duration: 0.16 } },
};

export const modalContent = {
  hidden: { opacity: 0, scale: 0.94, y: 12 },
  show:   { opacity: 1, scale: 1,    y: 0,
    transition: { duration: 0.30, ease: ease.expo } },
  exit:   { opacity: 0, scale: 0.96, y: 8,
    transition: { duration: 0.18 } },
};

export const sheetContent = {
  hidden: { opacity: 0, y: "100%" },
  show:   { opacity: 1, y: 0,
    transition: { duration: 0.38, ease: ease.expo } },
  exit:   { opacity: 0, y: "100%",
    transition: { duration: 0.26, ease: ease.in } },
};

// ── Card Hover Preset ─────────────────────────────────────────────────────────
export const cardHover = {
  rest:  { y: 0,  boxShadow: "var(--shadow-md)" },
  hover: { y: -3, boxShadow: "var(--shadow-xl)",
    transition: { duration: 0.30, ease: ease.expo } },
};

// ── Button Tap Preset ─────────────────────────────────────────────────────────
export const buttonTap = { scale: 0.97, transition: { duration: 0.10 } };

// ── Reveal — fade-up on viewport entry ───────────────────────────────────────
export function Reveal({
  children,
  className = "",
  delay    = 0,
  from     = "bottom",
  once     = true,
  duration: dur = 0.58,
  blur     = false,
}) {
  const ref   = useRef(null);
  const inView = useInView(ref, { once, margin: "-60px 0px" });

  const dirs = {
    bottom: { opacity: 0, y:  22, ...(blur && { filter: "blur(3px)" }) },
    top:    { opacity: 0, y: -20, ...(blur && { filter: "blur(3px)" }) },
    left:   { opacity: 0, x: -28 },
    right:  { opacity: 0, x:  28 },
    scale:  { opacity: 0, scale: 0.90 },
    fade:   { opacity: 0 },
  };
  const hidden  = dirs[from] || dirs.bottom;
  const visible = { opacity: 1, y: 0, x: 0, scale: 1, filter: "blur(0px)" };

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={hidden}
      animate={inView ? visible : hidden}
      transition={{ duration: dur, ease: ease.expo, delay }}
    >
      {children}
    </motion.div>
  );
}

// ── Stagger — reveal children sequentially on scroll ─────────────────────────
export function Stagger({
  children,
  className = "",
  fast  = false,
  slow  = false,
  delay = 0,
  once  = true,
}) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once, margin: "-60px 0px" });
  const container = slow
    ? staggerSlowContainer
    : fast
    ? staggerFastContainer
    : staggerContainer;

  return (
    <motion.div
      ref={ref}
      className={className}
      variants={container}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
}

// ── CountUp — animated number counter ────────────────────────────────────────
export function CountUp({ to, from = 0, duration: dur = 1.8, decimals = 0, prefix = "", suffix = "", className = "" }) {
  const [display, setDisplay] = useState(from);
  const ref     = useRef(null);
  const inView  = useInView(ref, { once: true, margin: "-40px 0px" });

  useEffect(() => {
    if (!inView) return;
    let start = null;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / (dur * 1000), 1);
      const eased    = 1 - Math.pow(1 - progress, 3); // cubic ease-out
      setDisplay(from + (to - from) * eased);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, from, to, dur]);

  const formatted = display.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={className}>
      {prefix}{formatted}{suffix}
    </span>
  );
}

// ── ParallaxFloat — subtle floating animation tied to scroll ─────────────────
export function ParallaxFloat({ children, speed = 0.15, className = "" }) {
  const ref = useRef(null);
  const y   = useMotionValue(0);
  const smoothY = useSpring(y, { stiffness: 100, damping: 30, mass: 0.5 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
    });
    observer.observe(el);
    const handleScroll = () => {
      const rect = el.getBoundingClientRect();
      const center = window.innerHeight / 2;
      y.set((rect.top + rect.height / 2 - center) * speed);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, [speed, y]);

  return (
    <motion.div ref={ref} style={{ y: smoothY }} className={className}>
      {children}
    </motion.div>
  );
}

// ── Reduced Motion ────────────────────────────────────────────────────────────
export function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(mq.matches);
    const handler = (e) => setPrefersReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return prefersReduced;
}

// Re-export framer-motion for convenience
export { motion, AnimatePresence, useInView, useMotionValue, useSpring, useTransform };
